<script setup lang="ts">
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  nothing,
  toDomAttr,
  toMaybe,
  type Maybe,
} from "../../lib/maybe";
import { useChapterHeadings } from "../../composables/useChapterHeadings";
import { computed } from "vue";
import { useSynopsisDraft } from "../../composables/useSynopsisDraft";
import type { BoardDragSource } from "../../stores/useStoryboardDragStore";
import { useStoryboardDragStore } from "../../stores/useStoryboardDragStore";
import StoryboardName from "./StoryboardName.vue";
import ZwTextarea from "../ui/ZwTextarea.vue";
import { IconGripVertical } from "../ribbonIcons";
import { type SceneOpening } from "../../lib/sceneLabel";
import SceneMetadata, { type StoryboardEntityChip } from "./SceneMetadata.vue";
export type { StoryboardEntityChip } from "./SceneMetadata.vue";

const { chapterLabel, canRenameChapter, chapterRenamePrefix } =
  useChapterHeadings();
const props = defineProps<{
  kind: "chapter" | "scene";
  /** Nothing = a nameless scene: the card is just its synopsis. */
  title: Maybe<string>;
  synopsis: string;
  /** Multi-scene chapter band headers omit synopsis; each scene card has its
   * own. Flat (≤1 scene) chapter cards keep the field. */
  hideSynopsis?: boolean;
  opening: SceneOpening;
  /** Reseed key for the synopsis debounce — e.g. a folder, or
   * `<folder>/<file>`. Must be stable for as long as this card represents
   * the same chapter/scene. */
  synopsisId: string;
  entityChips: StoryboardEntityChip[];
  dragSource: BoardDragSource;
  isDragOver: boolean;
  reorderEnabled: boolean;
  /** True for the one card actively being dragged — dimmed with a dashed
   * outline so it's unmistakable a drag is in progress and what's moving. */
  isDragSource: boolean;
  onCommitSynopsis: (id: string, value: string) => void | Promise<void>;
  onRename: (to: string) => void | Promise<void>;
  onCardPointerDown: (source: BoardDragSource, event: PointerEvent) => void;
  onKeyboardMove: (direction: "left" | "right" | "up" | "down") => void;
  onPark: () => void;
  /** Already confirms with the user and is Trash-recoverable (see
   * `onDeleteProjectFile`/`onDeleteProjectScene` in `useProjectWorkspace.ts`)
   * — no extra confirmation needed at the card level. */
  onDelete: () => void;
  /** `null` for scene cards — only a chapter (flat or band-header) card can
   * grow a new scene into itself. */
  onAddScene: Maybe<() => void>;
  onChipClick?: (chip: StoryboardEntityChip) => void;
  onArcChipPointerDown?: (
    chip: StoryboardEntityChip,
    event: PointerEvent,
  ) => void;
}>();

const drag = useStoryboardDragStore();

const emit = defineEmits<{ open: []; autosize: [] }>();

const { draft: synopsisDraft } = useSynopsisDraft({
  identity: computed(() => just(props.synopsisId)),
  sourceValue: () => props.synopsis,
  commit: (id, value) => props.onCommitSynopsis(id, value),
});

function onCardClick(): void {
  // A completed drag (including drop back on the same card) still synthesizes
  // a click on pointerup. Chips already suppress that; the card open path
  // must too, or a no-op reorder opens the scene.
  if (drag.consumePointerGestureWasDrag()) return;
  if (props.kind === "scene") emit("open");
}

function runAddScene(): void {
  if (isJust(props.onAddScene)) props.onAddScene.value();
}
</script>

<template>
  <div
    class="zw-board-card"
    :class="{
      'zw-board-card-chapter': kind === 'chapter',
      'zw-board-card-scene': kind === 'scene',
      'zw-board-card-nameless': isNothing(title),
      'zw-board-card-drag-over': isDragOver,
      'zw-board-card-drag-source': isDragSource,
    }"
    :data-board-key="dragSource.key"
    :data-board-kind="dragSource.kind"
    :data-board-folder="toDomAttr(dragSource.folder)"
    :data-board-act="toDomAttr(dragSource.actFolder)"
    :data-board-band="toDomAttr(dragSource.folder)"
    :data-board-scene="toDomAttr(dragSource.sceneFile)"
    :role="kind === 'scene' ? 'listitem' : 'group'"
    tabindex="0"
    :aria-keyshortcuts="
      reorderEnabled
        ? 'Alt+ArrowUp Alt+ArrowDown Alt+ArrowLeft Alt+ArrowRight Alt+P'
        : 'Alt+P'
    "
    @click="onCardClick"
    @keydown.enter="onCardClick"
    @keydown.alt.left="onKeyboardMove('left')"
    @keydown.alt.right="onKeyboardMove('right')"
    @keydown.alt.up="onKeyboardMove('up')"
    @keydown.alt.down="onKeyboardMove('down')"
    @keydown.alt.p="onPark"
  >
    <div class="zw-board-card-header">
      <span v-if="!reorderEnabled" class="zw-board-card-grip" aria-hidden="true"
        ><span class="zw-timeline-dot"
      /></span>
      <button
        v-if="reorderEnabled"
        type="button"
        class="zw-board-card-grip"
        aria-label="Drag to reorder"
        title="Drag to reorder — or select this card and press Alt+Arrow"
        @pointerdown.stop="onCardPointerDown(dragSource, $event)"
      >
        <IconGripVertical v-if="kind === 'chapter'" class="size-4" />
        <span v-else class="zw-timeline-dot" />
      </button>
      <div
        v-if="isJust(title) && kind === 'chapter'"
        class="zw-board-card-title"
        @click.stop
      >
        <StoryboardName
          v-if="canRenameChapter(synopsisId)"
          :prefix="chapterRenamePrefix(synopsisId)"
          :name="fromMaybe(title) ?? ''"
          label="Title"
          :on-rename="onRename"
          >{{ chapterLabel(synopsisId) }}</StoryboardName
        >
        <span v-else>{{ chapterLabel(synopsisId) }}</span>
      </div>
      <div
        v-if="isNothing(title) || kind === 'scene'"
        class="zw-board-card-synopsis zw-board-card-synopsis-lead"
        @click.stop
      >
        <ZwTextarea
          :id="nothing"
          v-model="synopsisDraft"
          variant="plain"
          autosize
          :rows="1"
          placeholder="Add synopsis..."
          aria-label="Synopsis"
          :label="nothing"
          @autosize="emit('autosize')"
        />
        <div v-if="opening.kind === 'text'" class="zw-board-card-synopsis-text">
          {{ opening.text }}
        </div>
      </div>
    </div>
    <div
      v-if="isJust(title) && kind === 'chapter' && !hideSynopsis"
      class="zw-board-card-synopsis"
      @click.stop
    >
      <ZwTextarea
        :id="nothing"
        v-model="synopsisDraft"
        variant="plain"
        autosize
        :rows="2"
        placeholder="Add synopsis..."
        aria-label="Synopsis"
        :label="nothing"
        @autosize="emit('autosize')"
      />
      <div v-if="opening.kind === 'text'" class="zw-board-card-synopsis-text">
        {{ opening.text }}
      </div>
    </div>
    <SceneMetadata
      class="zw-board-card-metadata"
      :scene-path="`Chapters/${synopsisId}`"
      :entity-chips="entityChips"
      :on-chip-click="(chip) => onChipClick?.(chip)"
      :on-arc-chip-pointer-down="toMaybe(onArcChipPointerDown)"
    />

    <div class="zw-board-card-actions">
      <button
        v-if="kind === 'scene'"
        type="button"
        class="zw-board-card-action"
        @click.stop="onCardClick"
      >
        Open scene
      </button>
      <button
        v-if="isJust(onAddScene)"
        type="button"
        class="zw-board-card-action"
        aria-label="Add scene"
        title="Add scene"
        @click.stop="runAddScene"
      >
        + Scene
      </button>
      <button
        type="button"
        class="zw-board-card-action"
        :aria-label="kind === 'chapter' ? 'Delete chapter' : 'Delete scene'"
        :title="kind === 'chapter' ? 'Delete chapter' : 'Delete scene'"
        @click.stop="onDelete"
      >
        Delete
      </button>
    </div>
  </div>
</template>

<style scoped>
.zw-board-card {
  position: relative;
  color: var(--text);
}
.zw-board-card-scene {
  display: grid;
  grid-template-columns: var(--timeline-axis) minmax(0, 1fr);
  padding: 24px 0;
}
.zw-board-card-header {
  grid-column: 2;
  padding-left: 24px;
}
.zw-board-card-grip {
  position: absolute;
  left: var(--timeline-axis);
  top: 24px;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: var(--bg);
  color: var(--text-muted);
  cursor: grab;
}
.zw-timeline-dot {
  width: 9px;
  height: 9px;
  border: 1px solid var(--color-zw-amber);
  border-radius: 50%;
  background: var(--bg);
}
.zw-board-card-grip:hover .zw-timeline-dot {
  background: var(--color-zw-amber);
}
.zw-board-card-synopsis {
  font-size: 0.9375rem;
}
.zw-board-card-synopsis-text {
  color: var(--text-muted);
  font-style: italic;
  margin-top: 8px;
}
.zw-board-card-metadata {
  grid-column: 1;
  grid-row: 1 / span 2;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  padding: 3px 24px 0 0;
}
.zw-board-card-actions {
  grid-column: 2;
  display: flex;
  gap: 16px;
  padding: 10px 0 0 24px;
}
.zw-board-card-action {
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
}
.zw-board-card-action:hover {
  color: var(--text);
}
.zw-board-card-drag-source,
.zw-board-card-chip-dragging {
  opacity: 0.45;
}
.zw-board-card-scene.zw-board-card-drag-over::before {
  content: "";
  position: absolute;
  left: var(--timeline-axis);
  right: 0;
  top: 0;
  height: 2px;
  background: var(--color-zw-amber);
}
.zw-board-card-chapter {
  min-width: 0;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg);
}
.zw-board-card-chapter .zw-board-card-header {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
}
.zw-board-card-chapter .zw-board-card-grip {
  flex-shrink: 0;
  position: static;
  transform: none;
  display: inline-flex;
  vertical-align: middle;
}
.zw-board-card-title {
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 1.1rem;
  min-width: 0;
}
.zw-board-card-chapter .zw-board-card-actions {
  flex-shrink: 0;
  padding: 0 0 0 16px;
}
.zw-board-card-chapter .zw-board-card-grip,
.zw-board-card-chapter .zw-board-card-actions {
  opacity: 0;
  pointer-events: none;
}
.zw-board-card-chapter:hover .zw-board-card-grip,
.zw-board-card-chapter:hover .zw-board-card-actions,
.zw-board-card-chapter:focus-within .zw-board-card-grip,
.zw-board-card-chapter:focus-within .zw-board-card-actions {
  opacity: 1;
  pointer-events: auto;
}
@media (hover: hover) {
  .zw-board-card-scene .zw-board-card-actions {
    opacity: 0;
    pointer-events: none;
  }
  .zw-board-card-scene:hover .zw-board-card-actions,
  .zw-board-card-scene:focus-within .zw-board-card-actions {
    opacity: 1;
    pointer-events: auto;
  }
}
@media (hover: none) {
  .zw-board-card-chapter .zw-board-card-grip,
  .zw-board-card-chapter .zw-board-card-actions {
    opacity: 1;
    pointer-events: auto;
  }
}
@container (max-width:560px) {
  .zw-board-card-metadata {
    grid-column: 2;
    grid-row: auto;
    align-items: flex-start;
    padding: 12px 0 0 24px;
  }
  .zw-board-card-chips {
    justify-content: flex-start;
  }
}
.zw-board-card-chapter.zw-board-card-drag-over {
  outline: 1px dashed var(--color-zw-amber);
}
</style>
